'use client';

import allowlistConfig from '@shared/all/helpers/domainAllowlist.config.json';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { client } from '../../utils/trpc';
type PreviewData = {
  title?: string;
  description?: string;
  image?: string;
} | null;
type ErrorState = {
  hasError: boolean;
  message: string;
  isRetryable: boolean;
} | null;
type Props = {
  url: string;
  compact?: boolean; // Smaller preview for notifications
  // Rendered when the preview can't be shown (loading, error, or no
  // metadata). Lets callers like LinksPanel hide the raw URL when the
  // preview works and fall back to the URL when it doesn't.
  fallback?: React.ReactNode;
};
const LinkPreview = ({
  url,
  compact = false,
  fallback = null
}: Props) => {
  const [previewData, setPreviewData] = useState<PreviewData>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ErrorState>(null);
  const [imageError, setImageError] = useState(false);
  const parseHTML = (html: string) => {
    const parser = new DOMParser();
    return parser.parseFromString(html, 'text/html');
  };
  const resolveUrl = (baseUrl: string, value?: string | null): string | undefined => {
    if (!value) return undefined;
    try {
      // If already absolute, new URL will succeed
      return new URL(value, baseUrl).toString();
    } catch {
      return undefined;
    }
  };
  const isHostAllowedForNextImage = (src?: string): boolean => {
    if (!src) return false;
    try {
      const {
        hostname,
        protocol
      } = new URL(src);
      if (!['http:', 'https:'].includes(protocol)) return false;
      const host = hostname.toLowerCase();
      if (allowlistConfig.exactDomains?.some(d => d.toLowerCase() === host)) {
        return true;
      }
      const patterns = allowlistConfig.imagePatternHostnames ?? [];
      for (const pat of patterns) {
        const reStr = '^' + pat.replace(/[.+?^${}()|\[\]\\]/g, '\\$&').replace(/\*\*/g, '.*').replace(/\*/g, '[^.]*') + '$';
        const re = new RegExp(reStr);
        if (re.test(host)) return true;
      }
      return false;
    } catch {
      return false;
    }
  };
  const isYouTubeURL = (url_0: string) => url_0.includes('youtube.com') || url_0.includes('youtu.be');
  const extractYouTubeVideoId = (url_1: string) => {
    const videoIdRegex = /(?:\/embed\/|\/watch\?v=|\/(?:embed\/|v\/|watch\?.*v=|youtu\.be\/|embed\/|v=))([^&?#]+)/;
    const match = url_1.match(videoIdRegex);
    return match ? match[1] : '';
  };
  const getRootUrl = (urlString: string): string => {
    try {
      const urlObj = new URL(urlString);
      return urlObj.origin; // e.g., "https://example.com"
    } catch {
      return urlString; // fallback to full URL if parsing fails
    }
  };
  const reportError = (errorMessage: string, errorUrl: string, error_0?: unknown) => {
    const rootUrl = getRootUrl(errorUrl);
    const errorStack = error_0 instanceof Error ? error_0.stack : typeof error_0 === 'string' ? error_0 : new Error().stack || 'No stack trace';
    void client.users.error.mutate({
      error: `LinkPreview Error [${rootUrl}]`,
      stack: `Error: ${errorMessage}\nURL: ${errorUrl}\n${errorStack}`,
      source: 'client'
    }).catch(() => {
      // Silently fail - error reporting should never throw
    });
  };
  const getErrorMessage = (status: number, errorText?: string): {
    message: string;
    isRetryable: boolean;
  } => {
    switch (status) {
      case 400:
        if (errorText?.includes('Invalid URL format')) {
          return {
            message: 'Invalid URL format',
            isRetryable: false
          };
        }
        if (errorText?.includes('does not return HTML content')) {
          return {
            message: 'URL does not contain previewable content',
            isRetryable: false
          };
        }
        return {
          message: 'Invalid request',
          isRetryable: false
        };
      case 403:
        if (errorText?.includes('Domain not allowed')) {
          return {
            message: 'This domain is not allowed for security reasons',
            isRetryable: false
          };
        }
        return {
          message: 'Access forbidden',
          isRetryable: false
        };
      case 408:
        return {
          message: 'Request timed out - the website is taking too long to respond',
          isRetryable: true
        };
      case 413:
        return {
          message: 'Website content is too large to preview',
          isRetryable: false
        };
      case 451:
        if (errorText?.includes('blocked for safety reasons')) {
          return {
            message: 'This domain is blocked for safety reasons',
            isRetryable: false
          };
        }
        return {
          message: 'This domain is blocked for safety reasons',
          isRetryable: false
        };
      case 502:
        if (errorText?.includes('Unable to connect')) {
          return {
            message: 'Unable to connect to the website',
            isRetryable: true
          };
        }
        if (errorText?.includes('Connection was reset')) {
          return {
            message: 'Connection was interrupted',
            isRetryable: true
          };
        }
        return {
          message: 'Website is currently unavailable',
          isRetryable: true
        };
      case 500:
        return {
          message: 'Preview service temporarily unavailable',
          isRetryable: true
        };
      default:
        return {
          message: 'Failed to load preview',
          isRetryable: true
        };
    }
  };
  const fetchPreviewData = async (url_2: string) => {
    try {
      setLoading(true);
      setError(null);

      // Lightweight client-side URL validation to avoid unnecessary API calls
      try {
        const parsed = new URL(url_2);
        if (!['http:', 'https:'].includes(parsed.protocol)) {
          setLoading(false);
          await reportError('Invalid URL format', url_2);
          setError({
            hasError: true,
            message: 'Invalid URL format',
            isRetryable: false
          });
          return;
        }
      } catch (error_2) {
        setLoading(false);
        await reportError('Invalid URL format', url_2, error_2);
        setError({
          hasError: true,
          message: 'Invalid URL format',
          isRetryable: false
        });
        return;
      }
      const response = await fetch(`/api/fetchLinkPreview?url=${encodeURIComponent(url_2)}`);
      if (!response.ok) {
        const errorData = await response.text();
        const errorInfo = getErrorMessage(response.status, errorData);
        // Only report failures of our own preview service (500). Other
        // statuses describe the external site (blocked, unreachable, too
        // large, ...) — expected conditions, not actionable errors.
        if (response.status === 500) {
          await reportError(`${errorInfo.message} (HTTP ${response.status})`, url_2, errorData);
        }
        setError({
          hasError: true,
          message: errorInfo.message,
          isRetryable: errorInfo.isRetryable
        });
        setLoading(false);
        return;
      }
      const data = await response.text();
      const doc = parseHTML(data);

      // Prefer OG/Twitter meta, then fallbacks
      const pickText = (...vals: Array<string | undefined | null>) => {
        for (const v of vals) if (v?.trim()) return v.trim();
        return undefined;
      };
      const title = pickText(doc.querySelector('meta[property="og:title"]')?.getAttribute('content') ?? undefined, doc.querySelector('meta[name="twitter:title"]')?.getAttribute('content') ?? undefined, doc.querySelector('title')?.textContent ?? undefined);
      const description = pickText(doc.querySelector('meta[property="og:description"]')?.getAttribute('content') ?? undefined, doc.querySelector('meta[name="twitter:description"]')?.getAttribute('content') ?? undefined, doc.querySelector('meta[name="description"]')?.getAttribute('content') ?? undefined);
      const imageRaw = pickText(doc.querySelector('meta[property="og:image:secure_url"]')?.getAttribute('content') ?? undefined, doc.querySelector('meta[property="og:image"]')?.getAttribute('content') ?? undefined, doc.querySelector('meta[name="twitter:image"]')?.getAttribute('content') ?? undefined);

      // Resolve possible relative URL
      let image = resolveUrl(url_2, imageRaw);
      if (isYouTubeURL(url_2)) {
        const videoId = extractYouTubeVideoId(url_2);
        image = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
      }

      // Favicon fallback if no image available
      if (!image) {
        const iconHref = doc.querySelector('link[rel="icon"]')?.getAttribute('href') || doc.querySelector('link[rel="shortcut icon"]')?.getAttribute('href') || doc.querySelector('link[rel="apple-touch-icon"]')?.getAttribute('href') || undefined;
        const resolvedIcon = resolveUrl(url_2, iconHref);
        if (resolvedIcon) image = resolvedIcon;else {
          try {
            const u = new URL(url_2);
            image = `https://${u.hostname}/favicon.ico`;
          } catch {
            // ignore
          }
        }
      }
      if (title || description || image) {
        setPreviewData({
          title,
          description,
          image
        });
      } else {
        // Page has no usable meta tags — an external-site property, not an
        // error worth reporting.
        setError({
          hasError: true,
          message: 'No preview information available',
          isRetryable: false
        });
      }
      setLoading(false);
    } catch (error_1) {
      setLoading(false);
      // Network failures are the user's connection, not a client bug — show
      // the message without reporting.
      setError({
        hasError: true,
        message: 'Network error - please check your connection',
        isRetryable: true
      });
      console.error('Failed to fetch link preview', error_1);
    }
  };
  useEffect(() => {
    void fetchPreviewData(url);
    setImageError(false); // Reset image error when URL changes
  }, [url]);
  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(url, '_blank');
  };

  // Render the fallback when preview can't be shown (loading, error, or
  // no metadata). Default fallback is null which preserves the prior
  // "render nothing" behavior for callers that don't pass one.
  if (loading || error || !previewData) {
    return <>{fallback}</>;
  }

  // Compact mode: smaller preview for notifications
  if (compact) {
    return <div onClick={handleLinkClick} className="mt-2 flex cursor-pointer items-center gap-2 rounded-lg border border-ds-border-subtle bg-background-light p-2 transition-colors duration-200 hover:bg-background-light">
        {previewData.image && !imageError && <div className="size-12 shrink-0 overflow-hidden rounded">
            {isHostAllowedForNextImage(previewData.image) ? <Image width={48} height={48} className="size-full object-cover" src={previewData.image} alt={previewData.title ?? ''} onError={() => {
          // External images 404 routinely (e.g. YouTube maxresdefault)
          // — fall back without reporting.
          setImageError(true);
        }} /> : <img className="size-full object-cover" src={previewData.image} alt={previewData.title ?? ''} loading="lazy" decoding="async" onError={() => {
          // External images 404 routinely (e.g. YouTube maxresdefault)
          // — fall back without reporting.
          setImageError(true);
        }} />}
          </div>}
        <div className="min-w-0 flex-1">
          {previewData.title && <p className="truncate text-xs font-emphasis text-textPrimary">
              {previewData.title}
            </p>}
          {previewData.description && <p className="line-clamp-1 text-xs text-textSecondary/70">
              {previewData.description}
            </p>}
        </div>
      </div>;
  }

  // Full mode: standard preview
  return <div onClick={handleLinkClick} className="mt-10 cursor-pointer rounded-lg border border-ds-border-subtle bg-background-light p-3 transition-colors duration-200 hover:bg-background-light">
      {previewData.title && <a href={url} rel="noopener noreferrer" className="text-primary transition-colors hover:text-indigo-400">
          {previewData.title}
        </a>}
      {previewData.description && <h6 className="mt-1 text-textSecondary/70">
          {previewData.description}
        </h6>}
      {previewData.image && !imageError && (isHostAllowedForNextImage(previewData.image) ? <Image width="0" height="0" sizes="100vw" className="mt-2 max-h-[360px] w-full rounded-md object-cover" src={previewData.image} alt={previewData.title ?? ''} onError={() => {
      // External images 404 routinely (e.g. YouTube maxresdefault)
      // — fall back without reporting.
      setImageError(true);
    }} /> : <img className="mt-2 max-h-[360px] w-full rounded-md object-cover" src={previewData.image} alt={previewData.title ?? ''} loading="lazy" decoding="async" onError={() => {
      // External images 404 routinely (e.g. YouTube maxresdefault)
      // — fall back without reporting.
      setImageError(true);
    }} />)}
    </div>;
};
export default LinkPreview;